import { ActionIcon, Anchor, Group, Stack, Text } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCopy } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { CreationForm } from "../../../components/form/CreationForm";
import { CREATE_APP_MUTATION, URL_REGEX } from "./queries";
import { useMutation } from "@apollo/client/react";

export function CreateAppForm() {
  const navigate = useNavigate();
  const clipboard = useClipboard();

  const [createApp] = useMutation<{
    createOrganizationApp: { id: string; secret: string };
  }>(CREATE_APP_MUTATION);

  return (
    <CreationForm
      title="Add app"
      schemaDefinition={{
        name: {
          type: "INPUT",
          inputType: "text",
          label: "Name",
          yupConfig: yup.string().required(),
          placeholder: "My app",
        },
        description: {
          type: "TEXTAREA",
          label: "Description",
          yupConfig: yup.string().required(),
        },
        loginUrl: {
          type: "INPUT",
          inputType: "text",
          label: "Login URL",
          yupConfig: yup
            .string()
            .matches(URL_REGEX)
            .required()
            .typeError("Must be a valid URL"),
          placeholder: "https://myapp.com/login",
        },
        consentUrl: {
          type: "INPUT",
          inputType: "text",
          label: "Consent URL",
          yupConfig: yup
            .string()
            .matches(URL_REGEX)
            .required()
            .typeError("Must be a valid URL"),
          placeholder: "https://myapp.com/consent",
        },
        logoutRedirectUrls: {
          type: "INPUT",
          inputType: "text",
          label: "Logout redirect URL",
          yupConfig: yup
            .string()
            .matches(URL_REGEX)
            .required()
            .typeError("Must be a valid URL"),
          placeholder: "https://myapp.com/logout",
        },
        redirectUrls: {
          type: "INPUT",
          inputType: "text",
          label: "Redirect URL",
          subtext:
            "URL where users will be redirected after successful authentication. It is important to note that the redirect URL is called ONLY if there are more than 2 identity providers set as 'login allowed' to true, because we are not able to know which one to use for the login process, if only one is available, this URL redirect will be skipped. If you have more than 1 identity provider set to 'allowed login', you will need to manage the login page yourself to allow for the identity provider selection",
          yupConfig: yup
            .string()
            .matches(URL_REGEX)
            .required()
            .typeError("Must be a valid URL"),
          placeholder: "https://myapp.com/redirect",
          info: (
            <>
              <b>Identity Linking:</b> To link external identities (e.g. Riot,
              Twitch) to existing accounts, use the IDP-based identity linking
              flow instead of the direct API.
              <br />
              <br />
              Configure a <b>linkRedirectUrl</b> on your identity provider
              (under Players &gt; Identity Providers), then call the{" "}
              <b>generateIdentityLinkUrl</b> mutation to get a redirect URL.
              <br />
              The user will be redirected through the WellPlayed IDP to the
              external provider, and back to your linkRedirectUrl with
              success/error query parameters.
            </>
          ),
        },
        requiresConsent: {
          type: "CHECKBOX",
          label: "Requires consent",
          defaultValue: false,
          yupConfig: yup.boolean().default(false),
        },
        public: {
          type: "CHECKBOX",
          label: "Public app",
          defaultValue: false,
          yupConfig: yup.boolean().default(false),
          info: (
            <>
              If checked, this app will allow for "Authorization Code Grant"
              <br />
              (i.e allows for your users to login), set to false if you want to
              <br />
              use "Client Credentials Grant" (i.e. allows for your app to login
              <br />
              as itself for machine to machine communication)
            </>
          ),
        },
      }}
      onSubmit={async (formData) => {
        const createdApp = await createApp({
          variables: {
            name: formData.name,
            description: formData.description,
            loginUrl: formData.loginUrl,
            consentUrl: formData.consentUrl,
            logoutRedirectUrls: [formData.logoutRedirectUrls],
            redirectUrls: [formData.redirectUrls],
            requiresConsent: formData.requiresConsent,
            public: formData.public,
          },
        }).then((res) => {
          if (res?.error) {
            throw new Error(res.error.message);
          }
          return res.data?.createOrganizationApp;
        });

        modals.open({
          title: "App created",
          onClose() {
            navigate("../" + createdApp?.id);
          },
          children: (
            <Stack>
              <Text>Your app has been created!</Text>
              <Group>
                <Text fw={700}>App ID:</Text> {createdApp?.id}{" "}
                <ActionIcon
                  onClick={() => {
                    clipboard.copy(createdApp?.id ?? "");
                  }}
                >
                  <IconCopy />
                </ActionIcon>
              </Group>
              <Text>
                <Anchor
                  onClick={() => {
                    clipboard.copy(createdApp?.secret ?? "");
                  }}
                >
                  Click here to copy your app secret
                </Anchor>
              </Text>
              <Text c="red">
                Please note that this is the only time you will be able to see
                your app secret, so make sure to copy it somewhere safe.
                <br />
                You will be able to reset your app secret at any time through
                the app settings.
              </Text>
            </Stack>
          ),
        });
      }}
    />
  );
}
